/**
 * Apollo client. The BFF is the only endpoint the client ever talks to —
 * domain services are not reachable from here by design (CLAUDE.md §3).
 *
 * The access token is attached per request from the in-memory store in
 * `auth.jsx`. The client still never sends its own actor id: identity is
 * derived from the token server-side.
 */
import { ApolloClient, HttpLink, InMemoryCache, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { getAccessToken } from './auth.jsx';
import { GRAPHQL_URL } from './runtimeEnv.js';

const uri = GRAPHQL_URL;

const authLink = setContext((_operation, { headers }) => {
  const token = getAccessToken();
  return { headers: token ? { ...headers, authorization: `Bearer ${token}` } : headers };
});

export const apolloClient = new ApolloClient({
  link: from([authLink, new HttpLink({ uri })]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: { fetchPolicy: 'cache-and-network' },
  },
});

/**
 * FORBIDDEN and UNAUTHENTICATED mean different things to a user: the first is
 * "you may not do this" (show a forbidden screen), the second is "your session
 * ended" (sign in again). Screens use these rather than matching on message
 * text, which would break the moment a message is reworded.
 */
export function errorCode(error) {
  return error?.graphQLErrors?.[0]?.extensions?.code ?? null;
}

export const isForbidden = (error) => errorCode(error) === 'FORBIDDEN';
export const isUnauthenticated = (error) => errorCode(error) === 'UNAUTHENTICATED';
